import React, { useEffect, useRef, useState } from "react";
import "../styles.css";
import { Field, Form, Formik, FormikHelpers, FormikProps } from "formik";
import * as Yup from "yup";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { userStore } from "store/UserStore";
import hidden from "../../../assets/images/hidden.png";
import eye from "../../../assets/images/eye.png";
import format from "string-template";
import {
  ACK_FAIL,
  ACK_SUCCESS,
  FORGOT_RESET_MESSAGE,
  OTP_VERIFY_FAILURE,
  OTP_VERIFY_SUCCESS,
} from "constants/messages.constant";
import ResetPasswordService from "../../resetPassword/services/resetPassword.service";
import LoginService from "pages/login/services/login.service";
import { useHistory } from "react-router-dom";
import OtpPopup from "shared/common/otp-modal/otpModal";
import OtpModal from "shared/common/modal/otp_modal";
import Loader from "shared/common/loader";

interface PropsType {
  handleModal: (show: boolean) => void;
  isShow: boolean;
}

interface OtpData {
  value: string;
  ackMessage: string;
  ackMessageType: "success" | "fail";
}

interface FormValues {
  new_password: string;
  confirm_password: string;
}

const ChangePassword: React.FC<PropsType> = ({ handleModal, isShow }) => {
  const history = useHistory();
  const formikRef = useRef<FormikProps<FormValues>>(null); // ✅ reference to Formik

  const [isLoading, setLoading] = useState(false);
  const [isShowNewPwd, setShowNewPwd] = useState(false);
  const [isShowCnfrmPwd, setShowCnfrmPwd] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState<boolean>(false);
  const [modalShow, setModalShow] = useState(false);

  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");

  const [txtSuscces, setTxtSuscces] = useState("");
  const [txtError, setTxtError] = useState("");
  const [destination, setDestination] = useState("");

  const [user, customer] = userStore((state: any) => [
    state.user,
    state.customer,
  ]);

  const [otpData, setOtpData] = useState<OtpData>({
    value: "",
    ackMessage: "",
    ackMessageType: "success",
  });

  useEffect(() => {
    if (user?.mobile?.value) {
      setMobile(user?.mobile?.value);
    }
  }, [customer]);

  useEffect(() => {
    if (otpData.value?.length === 6) {
      confirm();
    }
  }, [otpData.value]);

  const resetOtpData = () => {
    setOtpData({ value: "", ackMessage: "", ackMessageType: "success" });
  };

  const sendOtp = async () => {
    try {
      const res: any = await LoginService.sendOTP(mobile);
      if (res) {
        setTxtError("");
        setTxtSuscces("OTP has been sent to the registered mobile");
        setModalShow(true);
        setIsOtpVerified(false);
      }
    } catch (err: any) {
      toast.error(err.message);
      setTxtSuscces("");
      setTxtError("There was an error in sending the OTP");
    }
  };

  const onSuccess = async (
    otp: any,
    val: FormValues,
    resetForm?: () => void
  ) => {
    setPassword(val.new_password);
    try {
      const res: any = await ResetPasswordService.resetPassword(
        mobile,
        otp,
        val.new_password
      );
      if (res) {
        setTxtError("");
        setTxtSuscces("OTP has been sent to your registered mobile");
        setModalShow(true);
        setDestination(res?.CodeDeliveryDetails?.Destination || "");
        setIsOtpVerified(false);

        if (otpData.value?.length === 6) {
          confirm(resetForm);
        }
      }
    } catch (err: any) {
      setDestination("");
      console.log(err);
      toast.error(err.message);
    }
  };

  const confirm = async (resetForm?: () => void) => {
    try {
      setLoading(true);
      const res: any = await ResetPasswordService.confirmPassword(
        mobile,
        otpData?.value,
        password
      );
      setLoading(false);
      if (res) {
        setOtpData({
          ...otpData,
          ackMessage: OTP_VERIFY_SUCCESS,
          ackMessageType: ACK_SUCCESS,
        });
        toast.success("Password has been reset successfully.", {
          autoClose: 3000,
        });

        // ✅ reset form after success
        if (resetForm) resetForm();
        formikRef.current?.resetForm();

        setPassword("");
        resetOtpData();
        setDestination("");
        setIsOtpVerified(true);
        setModalShow(false);
        handleModal(false);

        setTimeout(() => history.push("/profile"), 3000);
      }
    } catch (err: any) {
      setLoading(false);
      setOtpData({
        value: "",
        ackMessage: OTP_VERIFY_FAILURE,
        ackMessageType: ACK_FAIL,
      });
      console.log(err);
      toast.error(err.message);
    }
  };

  const handleClose = () => {
    // ✅ reset form when closing the modal
    formikRef.current?.resetForm();
    resetOtpData();
    handleModal(false);
  };

  return (
    <>
      {isLoading && <Loader />}
      <div
        onClick={handleClose}
        className={`Backdrop ${isShow ? "BackdropActive" : ""}`}
      />
      <div
        className={`Sidebar ${isShow ? "Open" : ""}`}
        style={{ width: "420px" }}
      >
        <div className="modal-header" style={{ margin: '40px 10px 0 10px' }}>
          <h4 className="modal-title font-weight-bold letter"  style={{ letterSpacing: '1px' }}>Set Password</h4>
          <span aria-hidden="true" className="Close" onClick={handleClose}>
            &times;
          </span>
        </div>

        <div className="p-5">
          <Formik
            innerRef={formikRef} // ✅ keeps a ref to Formik
            initialValues={{ new_password: "", confirm_password: "" }}
            enableReinitialize
            onSubmit={(
              values: FormValues,
              { setSubmitting, resetForm }: FormikHelpers<FormValues>
            ) => {
              setSubmitting(false);
              onSuccess("0", values, resetForm);
            }}
            validationSchema={Yup.object({
              new_password: Yup.string()
                .required("Password is required")
                .matches(
                  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
                  "The password must be at least 8 characters and it must contain at least one uppercase, one lowercase, one number and one special character."
                ),
              confirm_password: Yup.string()
                .oneOf(
                  [Yup.ref("new_password"), ""],
                  "Password & Confirm password must match"
                )
                .required("Confirm your password"),
            })}
          >
            {({ values, errors }) => {
              const isButtonDisabled =
                isLoading ||
                !values.new_password ||
                !values.confirm_password ||
                !!errors.new_password ||
                !!errors.confirm_password;

              return (
                <Form>
                  <div className="form-group mb-2">
                    <label className="form-label">New Password</label>
                    <div className="input-group mb-1">
                      <Field
                        type={isShowNewPwd ? "text" : "password"}
                        name="new_password"
                        className={`form-control ${
                          errors.new_password ? "is-invalid" : ""
                        }`}
                        autoComplete="off"
                      />
                      <div className="input-group-text">
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowNewPwd(!isShowNewPwd);
                          }}
                        >
                          <img
                            className="m-2"
                            style={{ width: "16px" }}
                            src={isShowNewPwd ? hidden : eye}
                            alt="toggle password"
                          />
                        </a>
                      </div>
                    </div>
                    {errors.new_password && (
                      <div className="text-danger small">
                        {errors.new_password}
                      </div>
                    )}
                  </div>

                  <div className="form-group mb-2">
                    <label className="form-label">Confirm Password</label>
                    <div className="input-group mb-1">
                      <Field
                        type={isShowCnfrmPwd ? "text" : "password"}
                        name="confirm_password"
                        className={`form-control ${
                          errors.confirm_password ? "is-invalid" : ""
                        }`}
                        autoComplete="off"
                      />
                      <div className="input-group-text">
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowCnfrmPwd(!isShowCnfrmPwd);
                          }}
                        >
                          <img
                            className="m-2"
                            style={{ width: "16px" }}
                            src={isShowCnfrmPwd ? hidden : eye}
                            alt="toggle password"
                          />
                        </a>
                      </div>
                    </div>
                    {errors.confirm_password && (
                      <div className="text-danger small">
                        {errors.confirm_password}
                      </div>
                    )}
                  </div>

                  <Button
                    disabled={isButtonDisabled}
                    className="w-100 mt-3"
                    variant="primary"
                    type="submit"
                  >
                    {!isLoading ? "Change" : "Processing..."}
                  </Button>
                </Form>
              );
            }}
          </Formik>
        </div>

        {/* OTP Modals */}
        <OtpModal
          show={modalShow}
          handleClose={() => setModalShow(false)}
          title="Enter the OTP sent to your registered mobile number"
          onSuccess={() => confirm()}
          resend={sendOtp}
          txtSuscces={txtSuscces}
          txtError={txtError}
          isVerified={isOtpVerified}
          loading={isLoading}
        />

        <OtpPopup
          show={modalShow}
          isLoading={isLoading}
          handleClose={() => {
            resetOtpData();
            setModalShow(false);
          }}
          modalTitle="User Password Reset Confirmation"
          modalSubmitBtnText="Verify"
          onSubmit={() => confirm()}
          otpLabel={format(FORGOT_RESET_MESSAGE, { mobile: destination })}
          enableResendTimer={true}
          resendHandler={() => {
            resetOtpData();
            sendOtp();
          }}
          otpChangeHandler={(value) =>
            setOtpData({ value, ackMessage: "", ackMessageType: "success" })
          }
          resendButtonText="Resend OTP"
          ackMessage={otpData.ackMessage}
          ackMessageType={otpData.ackMessageType}
          numInputs={6}
          otpValue={otpData.value}
          resendInterval={30}
        />
      </div>
    </>
  );
};

export default ChangePassword;
